// English UI dictionary for the QR Phone caller flow and staff softphone.
//
// This is the single source of truth for every user-facing string in the app
// (titles, subtitles, buttons, placeholders, status messages, toasts, aria-labels,
// and the user-visible call-status texts produced by the signaling core).
//
// Parameterised strings are exposed as functions (e.g. queuePosition) so callers
// pass the runtime value and the wording — including word order — stays in this file
// when other languages are added.
//
// Adding another language later: copy this file to (for example) `es.ts`, translate
// the values, keep the keys and function signatures identical, then register it in
// `index.ts`. No component needs to change.
export const en = {
  // BRAND
  brand: 'QR Phone',

  // ---------- CALLER: LANDING + FORM (ScanLanding) ----------
  landingDefaultTitle: 'Video call',
  landingIntro: 'A member of our team will assist you by video call in just a moment.',
  startCall: '📞 Start call',
  loading: 'Loading…',

  // CALLER: FORM VALIDATION (ScanLanding, StaffNotAvailable)
  fieldRequired: 'Please enter this information to continue.',
  invalidEmail: 'Please enter a valid email address.',
  invalidPhone: 'Please enter a valid phone number.',

  // CALLER: PERMISSIONS (ScanLanding)
  preparingCall: 'Preparing your call…',
  permissionsTitle: 'Permissions required',
  permissionsIntro: 'To assist you, we need access to your microphone and camera. Your browser will ask you to confirm.',
  requestingPermissions: 'Requesting permissions…',
  allowAndContinue: 'Allow and continue',
  back: 'Back',
  permissionsDenied:
    'We could not access your camera and microphone. Please enable the permissions in your browser (the lock icon next to the address) and try again.',

  // CALLER: EXPIRED / INVALID / UNAVAILABLE (ScanLanding, InvalidQr)
  qrExpiredTitle: 'This code is no longer valid',
  qrExpiredIntro:
    'The code you scanned has expired or is not correct. Please try again later or ask a member of staff at the venue.',
  invalidQrTitle: 'Invalid code',
  invalidQrIntro:
    'The code you scanned is not valid. Please scan the code again or ask a member of staff at the venue.',
  serviceUnavailableTitle: 'Service unavailable',
  serviceUnavailablePrepare:
    'We could not set up the call. Please check your internet connection and try again in a few moments.',
  serviceInactive: 'This service is not active at the moment. Please try again later.',
  notYetAvailableTitle: 'Not yet available',
  availableFrom: (when: string) => `This service will be available from ${when}.`,
  retry: 'Try again',

  // ---------- CALLER: IN-CALL / QUEUE (CallView, CallScreen) ----------
  checkConnection: 'Please check your internet connection.',
  connectingCall: 'We are connecting your call. Please wait…',
  queuePosition: (n: number | string) => `Position in queue: ${n}`,
  cancel: 'Cancel',
  beingAssisted: 'You are being assisted',
  cameraOff: 'Camera off',
  unstableConnection: 'Unstable connection. Trying to recover the call…',
  micOff: '🔇 Microphone off',

  // CALLER: NO STAFF (CallView failure panel)
  cannotAssistTitle: 'We were unable to assist you',
  allBusy: 'All of our team members are busy right now. Please try again in a few minutes.',

  // ---------- CALLER: CALL ENDED (CallEnded) ----------
  callEndedTitle: 'Call ended',
  callDuration: (formatted: string) => `Duration: ${formatted}`,
  thanksForUsing: 'Thank you for using this service.',
  needAnythingElse: 'Do you need anything else?',
  callAgain: 'Call again',
  youMayCloseScreen: 'You may now close this screen.',

  // ---------- CALLER: CALL ERROR (CallError) ----------
  callErrorTitle: 'We could not complete the call',
  callErrorIntro: 'There was a problem during the call. Please try again in a few minutes.',

  // ---------- CALLER: CALLBACK / NO STAFF (StaffNotAvailable) ----------
  callbackIntro:
    'All of our team members are busy at the moment. If you wish, leave us a contact detail and we will get back to you as soon as possible.',
  callbackWantContact: 'Would you like us to contact you?',
  yes: 'Yes',
  no: 'No',
  callbackDeclined: 'That is fine. We hope to assist you soon. You may now close this screen.',
  callbackPreferredMethod: 'How would you prefer us to contact you?',
  emailLabel: 'Email address',
  phoneLabel: 'Phone number',
  send: 'Send',
  callbackThanks:
    'Thank you. We have registered your details and will get back to you as soon as possible.',
  // Shown instead of the contact form on public kiosks (no keyboard).
  callbackKioskMessage:
    'All of our team members are busy at the moment. Please try again shortly, or ask a member of staff at the venue.',

  // ---------- SHARED IN-CALL CONTROL BAR (CallControls aria-labels) ----------
  muteMic: 'Mute microphone',
  unmuteMic: 'Unmute microphone',
  turnOffCamera: 'Turn off camera',
  turnOnCamera: 'Turn on camera',
  switchCamera: 'Switch camera',
  hangUp: 'Hang up',

  // ---------- STAFF: LOGIN (StaffLogin) ----------
  staffLoginTitle: 'Staff access',
  staffLoginIntro: 'Enter your details to receive calls on this device.',
  staffRedirecting: 'Redirecting to sign-in…',
  passwordLabel: 'Password',
  signingIn: 'Signing in…',
  signIn: 'Sign in',
  loginFailedGeneric: 'We could not sign you in. Please try again.',
  loginFailedCheckDetails: 'We could not sign you in. Please check your details and try again.',
  loginFailedService: 'We could not connect to the service. Please try again.',
  // STAFF: SIGNED IN ELSEWHERE (single-device prompt)
  otherDeviceTitle: 'Already signed in elsewhere',
  otherDeviceBody:
    'This account is currently in use on another device. To continue here, the other device will be signed out.',
  otherDeviceConfirm: 'Continue here',
  otherDeviceCancel: 'Cancel',

  // ---------- STAFF: DASHBOARD (StaffDashboard) ----------
  staffAvailable: 'Available for calls',
  staffReconnecting: 'Reconnecting…',
  staffAvailableHint: 'Keep this screen open. We will let you know when you receive a call.',
  staffReconnectingHint: 'We are restoring the connection. Please do not close this screen.',
  staffNotificationsHelp:
    'To receive call alerts while this screen is in the background, enable notifications.',
  staffEnableNotifications: 'Enable alerts',
  staffLogout: 'Sign out',

  // ---------- STAFF: IN-CALL (StaffCallView) ----------
  staffDefaultCaller: 'Caller',
  staffConnectingCall: 'Connecting the call…',
  staffCustomerMicOff: '🔇 Caller microphone off',

  // ---------- STAFF: INCOMING CALL (StaffIncomingCall) ----------
  staffNoActiveCall: 'There is no active call.',
  staffNoIncomingCall: 'There is no incoming call at the moment.',
  staffIncomingTitle: 'Incoming call',
  staffReject: 'Decline',
  staffAccept: 'Accept',
  staffAlreadyAnswered: 'Another team member has already taken this call.',

  // ---------- PUBLIC STATIC / LEGAL PAGES (rehosted from the legacy frontend) ----------
  // Page chrome only. The verbatim legal bodies live inline in their components.
  privacyPolicyTitle: 'Privacy Policy',
  deleteAccountTitle: 'How to delete account',

  // CONTACT PAGE (Contact) → POST /users/contactMail
  contactTitle: 'Contact us',
  contactIntro: 'Send us your query and we will get back to you as soon as possible.',
  contactNameLabel: 'Name',
  contactMessageLabel: 'Message',
  contactPrivacyConsent: 'I have read and agree to the',
  contactPrivacyRequired: 'Please accept the privacy policy to continue.',
  contactPromotionConsent: 'I would like to receive commercial information and promotional offers by email.',
  contactSubmit: 'Submit',
  contactSending: 'Please wait…',
  contactSuccess: 'Thank you. Your message has been sent.',
  contactError: 'We could not send your message. Please try again.',

  // LANDING PAGE (Landing)
  landingHeroTitle: 'Scan, connect, video call',
  landingHeroSubtitle: 'Instant video calls with a simple QR code scan.',
  landingCard1Title: 'For personal use',
  landingCard1Body: 'Share a QR code, not your phone number. You can activate and deactivate your QR codes at any time.',
  landingCard2Title: 'For companies',
  landingCard2Body: 'Invite your staff and create schedules. Give a human touch to your customers.',
  landingCard3Title: 'No downloads needed',
  landingCard3Body: 'No app is required for callers. By scanning the QR code, a web page with the call opens up.',

  // ---------- CALLER: USER-VISIBLE CALL-STATUS TEXTS (core/VideoState) ----------
  statusConnecting: 'We are connecting your call. Please wait…',
  statusCalling: 'Calling…',
  statusWillAssistShortly: 'We will assist you in a moment…',
  statusEveryoneBusy:
    'All of our team members are assisting other calls. Please do not close this screen; we will attend to you as soon as possible.',
  cameraActivationFailed: 'We could not turn on the camera. Please check your connection and camera permissions.',
} as const

export type Dictionary = typeof en
